import { useId, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { FileText, Info, Loader2, Plus, Presentation, RotateCcw, Search, Sheet, X, type LucideIcon } from "lucide-react";
import { SuiteAppTile } from "@athavita/ui-theme";
import type { DriveItem, DriveTemplate, TemplateApp } from "@/api/driveApi";
import { useTemplates } from "@/api/hooks";
import { Button } from "@/components/ui/button";
import { useFocusTrap } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { chipClass } from "../items/ViewControls";

type Tab = "all" | TemplateApp;

const GROUPS: { id: TemplateApp; label: string; icon: LucideIcon; kind: string }[] = [
    { id: "docs", label: "Documents", icon: FileText, kind: "Word" },
    { id: "sheets", label: "Spreadsheets", icon: Sheet, kind: "Excel" },
    { id: "slides", label: "Presentations", icon: Presentation, kind: "PowerPoint" }
];
const KIND: Record<TemplateApp, string> = { docs: "Word", sheets: "Excel", slides: "PowerPoint" };

/**
 * "Start something new" — blank files plus ready-made Word / Excel /
 * PowerPoint templates. Picking one copies it into the current folder and
 * opens it; the dialog closes once the copy exists.
 */
export function TemplateGallery(props: {
    /** Where copies land ("My Drive" or the folder's name). */
    folderName: string;
    onClose: () => void;
    /** Resolves the created file, or null when it failed (already toasted). */
    onPick: (t: DriveTemplate) => Promise<DriveItem | null>;
}) {
    return createPortal(<GalleryInner {...props} />, document.body);
}

function GalleryInner({
    folderName,
    onClose,
    onPick
}: {
    folderName: string;
    onClose: () => void;
    onPick: (t: DriveTemplate) => Promise<DriveItem | null>;
}) {
    const panelRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const titleId = useId();
    useFocusTrap(panelRef);
    const templates = useTemplates();
    const [tab, setTab] = useState<Tab>("all");
    /** null = all industries. */
    const [industry, setIndustry] = useState<string | null>(null);
    const [query, setQuery] = useState("");
    const [busyId, setBusyId] = useState<string | null>(null);

    const q = query.trim().toLowerCase();
    const catalogue = templates.data;
    const industries = useMemo(() => catalogue?.industries ?? [], [catalogue]);
    const { blanks, sections, counts, total } = useMemo(() => {
        const list = catalogue?.templates ?? [];
        const pool = list.filter(
            (t) =>
                (tab === "all" || t.app === tab) &&
                (!q || [t.name, t.category, t.description, t.fileName, t.industry].some((s) => (s ?? "").toLowerCase().includes(q)))
        );
        const filled = pool.filter((t) => !t.blank);
        const perIndustry = new Map<string, number>();
        for (const t of filled) perIndustry.set(t.industry, (perIndustry.get(t.industry) ?? 0) + 1);
        return {
            // Blank files lead every view (only the chosen app's when a type chip is on).
            blanks: pool.filter((t) => t.blank).sort(byApp),
            sections: industries
                .filter((i) => industry === null || i === industry)
                .map((i) => ({ industry: i, items: filled.filter((t) => t.industry === i).sort(byApp) }))
                .filter((s) => s.items.length > 0),
            counts: perIndustry,
            total: filled.length
        };
    }, [catalogue, industries, industry, q, tab]);

    // A changed filter starts the results from the top.
    const chooseIndustry = (next: string | null) => {
        setIndustry(next);
        scrollRef.current?.scrollTo({ top: 0 });
    };
    const chooseTab = (next: Tab) => {
        setTab(next);
        scrollRef.current?.scrollTo({ top: 0 });
    };
    const filtered = industry !== null || tab !== "all" || q !== "";
    const clearFilters = () => {
        setQuery("");
        setTab("all");
        chooseIndustry(null);
    };

    const pick = (t: DriveTemplate) => {
        if (busyId) return;
        setBusyId(t.id);
        // Called synchronously from the click so the editor tab can open.
        void onPick(t).then((item) => {
            if (item) onClose();
            else setBusyId(null);
        });
    };

    const card = (t: DriveTemplate) => (
        <TemplateCard key={t.id} t={t} busy={busyId === t.id} disabled={busyId !== null} onPick={() => pick(t)} />
    );

    let body;
    if (templates.isPending) {
        body = (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3.5" aria-busy="true" aria-label="Loading templates">
                {Array.from({ length: 8 }, (_, i) => (
                    <div key={i} className="space-y-2">
                        <div className="aspect-[4/3] animate-pulse rounded-md bg-muted" />
                        <div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
                    </div>
                ))}
            </div>
        );
    } else if (templates.isError) {
        body = (
            <div className="flex flex-col items-center gap-3 py-16 text-center text-sm text-muted-foreground">
                <p>Couldn't load templates. {templates.error.message}</p>
                <Button variant="outline" size="sm" onClick={() => void templates.refetch()}>
                    <RotateCcw className="h-4 w-4" /> Try again
                </Button>
            </div>
        );
    } else {
        const typeLabel = tab === "all" ? "templates" : (GROUPS.find((g) => g.id === tab)?.label.toLowerCase() ?? "templates");
        const where = industry !== null ? <> in <b className="font-semibold text-foreground">{industry}</b></> : null;
        body = (
            <>
                {blanks.length > 0 ? (
                    <section aria-labelledby={`${titleId}-blank`}>
                        <h3 id={`${titleId}-blank`} className="mb-2.5 mt-1 text-[13px] font-semibold">Blank</h3>
                        <div className="mb-5 grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3.5">{blanks.map(card)}</div>
                    </section>
                ) : null}
                {sections.map((s, i) => (
                    <section key={s.industry} aria-labelledby={`${titleId}-ind-${i}`}>
                        <h3 id={`${titleId}-ind-${i}`} className="mb-2.5 mt-1 flex items-center gap-2 text-[13px] font-semibold">
                            {s.industry}
                            <span className="text-xs font-normal text-[var(--drive-text-3)]">{s.items.length}</span>
                        </h3>
                        <div className="mb-5 grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3.5">{s.items.map(card)}</div>
                    </section>
                ))}
                {sections.length === 0 ? (
                    <div role="status" className="flex flex-col items-center gap-3 py-14 text-center text-sm text-muted-foreground">
                        <p>
                            {q ? (
                                <>No {typeLabel} match “{query.trim()}”{where}.</>
                            ) : filtered ? (
                                <>No {typeLabel}{where} yet.</>
                            ) : (
                                "No templates here yet."
                            )}
                        </p>
                        {filtered ? (
                            <Button variant="outline" size="sm" onClick={clearFilters}>
                                Clear filters
                            </Button>
                        ) : null}
                    </div>
                ) : null}
            </>
        );
    }

    const industryLabel = (i: string | null) => (i === null ? "All industries" : i);
    const industryCount = (i: string | null) => (i === null ? total : (counts.get(i) ?? 0));
    const options: (string | null)[] = [null, ...industries];

    return (
        <div
            data-drive-overlay=""
            className="drive-fade-in fixed inset-0 z-[60] flex items-center justify-center bg-black/40 sm:p-4"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
            onKeyDown={(e) => {
                // Keep keystrokes away from list / page shortcuts.
                e.stopPropagation();
                if (e.key === "Escape") {
                    e.preventDefault();
                    onClose();
                }
            }}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                className="drive-pop-in flex h-full w-full flex-col overflow-hidden bg-card text-card-foreground shadow-[var(--drive-shadow-28)] outline-none sm:h-[min(680px,calc(100vh-80px))] sm:w-[min(1000px,calc(100vw-32px))] sm:rounded-[10px]"
            >
                <header className="flex flex-wrap items-center gap-3 px-[18px] pb-2.5 pt-3.5">
                    <h2 id={titleId} className="text-xl font-semibold leading-[26px]">Start something new</h2>
                    <div className="relative order-last w-full sm:order-none sm:ml-auto sm:w-[300px]">
                        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--drive-text-2)]" aria-hidden />
                        <input
                            type="search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search templates"
                            aria-label="Search templates"
                            data-autofocus=""
                            className="h-8 w-full rounded-[4px] border-0 bg-[var(--drive-subtle)] pl-8 pr-3 text-sm text-foreground outline-none placeholder:text-[var(--drive-text-3)] focus-visible:ring-2 focus-visible:ring-ring"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="ml-auto flex h-9 w-9 items-center justify-center rounded-[4px] text-[var(--drive-text-2)] hover:bg-[var(--drive-hover)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:ml-0"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </header>
                <div className="flex flex-wrap items-center gap-1.5 border-b border-[var(--drive-line)] px-[18px] pb-3">
                    <div role="toolbar" aria-label="Template type" className="flex flex-wrap gap-1.5">
                        <button type="button" aria-pressed={tab === "all"} onClick={() => chooseTab("all")} className={chipClass(tab === "all")}>
                            All
                        </button>
                        {GROUPS.map((g) => (
                            <button key={g.id} type="button" aria-pressed={tab === g.id} onClick={() => chooseTab(g.id)} className={chipClass(tab === g.id)}>
                                <g.icon className="h-4 w-4" aria-hidden />
                                {g.label}
                            </button>
                        ))}
                    </div>
                    {/* Narrow screens: the industry rail becomes a native select. */}
                    <select
                        aria-label="Industry"
                        value={industry ?? ""}
                        onChange={(e) => chooseIndustry(e.target.value || null)}
                        disabled={industries.length === 0}
                        className="mt-1 h-8 w-full rounded-[4px] border border-[var(--drive-line-strong)] bg-card px-2 text-[13px] text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring sm:hidden"
                    >
                        {options.map((i) => (
                            <option key={i ?? ""} value={i ?? ""}>
                                {industryLabel(i)} ({industryCount(i)})
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex min-h-0 flex-1">
                    {industries.length > 0 ? (
                        <IndustryRail
                            options={options}
                            value={industry}
                            label={industryLabel}
                            count={industryCount}
                            onChange={chooseIndustry}
                        />
                    ) : null}
                    <div ref={scrollRef} className="min-h-0 min-w-0 flex-1 overflow-auto px-[18px] pb-6 pt-4">{body}</div>
                </div>
                <footer className="flex items-center gap-2.5 border-t border-[var(--drive-line)] bg-[var(--drive-pane)] px-[18px] py-2.5 text-xs text-[var(--drive-text-2)]">
                    <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    <span>
                        Opening a template makes a copy in <b className="font-semibold text-foreground">{folderName}</b> — the template itself never changes.
                    </span>
                </footer>
            </div>
        </div>
    );
}

const APP_ORDER: Record<TemplateApp, number> = { docs: 0, sheets: 1, slides: 2 };
/** Docs, then Sheets, then Slides; catalogue order within an app (sort is stable). */
const byApp = (a: DriveTemplate, b: DriveTemplate) => APP_ORDER[a.app] - APP_ORDER[b.app];

/**
 * Left rail of industries (sm and up). A radio group with one tab stop:
 * ↑ / ↓ / Home / End move and select, like a list of radio buttons.
 */
function IndustryRail({
    options,
    value,
    label,
    count,
    onChange
}: {
    options: (string | null)[];
    value: string | null;
    label: (i: string | null) => string;
    count: (i: string | null) => number;
    onChange: (i: string | null) => void;
}) {
    const listRef = useRef<HTMLDivElement>(null);
    const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        const at = options.indexOf(value);
        let next: number;
        if (e.key === "ArrowDown") next = Math.min(at + 1, options.length - 1);
        else if (e.key === "ArrowUp") next = Math.max(at - 1, 0);
        else if (e.key === "Home") next = 0;
        else if (e.key === "End") next = options.length - 1;
        else return;
        e.preventDefault();
        onChange(options[next] ?? null);
        listRef.current?.querySelectorAll<HTMLElement>('[role="radio"]')[next]?.focus();
    };
    return (
        <div
            ref={listRef}
            role="radiogroup"
            aria-label="Industry"
            onKeyDown={onKeyDown}
            className="hidden w-[212px] shrink-0 flex-col gap-px overflow-y-auto border-r border-[var(--drive-line)] px-2 py-3 sm:flex"
        >
            {options.map((i) => {
                const on = i === value;
                const n = count(i);
                return (
                    <button
                        key={i ?? ""}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        tabIndex={on ? 0 : -1}
                        onClick={() => onChange(i)}
                        className={cn(
                            "flex min-h-8 w-full shrink-0 items-center gap-2 rounded-[4px] px-2.5 py-1 text-left text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                            on ? "bg-[var(--drive-primary-soft)] font-semibold text-primary" : "text-foreground hover:bg-[var(--drive-hover)]",
                            !on && n === 0 && "text-[var(--drive-text-3)]"
                        )}
                    >
                        <span className="min-w-0 flex-1 truncate">{label(i)}</span>
                        <span className={cn("text-xs tabular-nums", on ? "text-primary" : "text-[var(--drive-text-3)]")}>{n}</span>
                    </button>
                );
            })}
        </div>
    );
}

function TemplateCard({ t, busy, disabled, onPick }: { t: DriveTemplate; busy: boolean; disabled: boolean; onPick: () => void }) {
    const name = t.blank ? `Blank ${t.name.toLowerCase()}` : t.name;
    return (
        <button
            type="button"
            onClick={onPick}
            disabled={disabled}
            aria-busy={busy || undefined}
            title={t.description}
            className="group flex min-w-0 flex-col gap-2 rounded-lg text-left outline-none disabled:cursor-wait"
        >
            <span
                className={cn(
                    "relative grid aspect-[4/3] w-full place-items-center overflow-hidden rounded-md border border-[var(--drive-line)] bg-[var(--drive-subtle)] transition-[box-shadow,border-color]",
                    "group-hover:border-primary group-hover:shadow-[var(--drive-shadow-8)] group-focus-visible:ring-2 group-focus-visible:ring-ring group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-card",
                    t.blank && "border-dashed bg-card text-[var(--drive-text-3)]"
                )}
            >
                {t.blank ? <Plus className="h-5 w-5" aria-hidden /> : <TemplateArt t={t} />}
                {busy ? (
                    <span className="absolute inset-0 grid place-items-center bg-card/70">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" aria-label="Creating" />
                    </span>
                ) : null}
            </span>
            <span className="flex min-w-0 items-center gap-1.5 px-0.5 text-[13px] font-semibold">
                <SuiteAppTile app={t.app} size={16} />
                <span className="truncate">{name}</span>
            </span>
            {!t.blank ? (
                <span className="-mt-1.5 truncate px-0.5 text-[11.5px] text-[var(--drive-text-3)]" title={`${t.industry} · ${t.category}`}>
                    {t.category} · {KIND[t.app]}
                </span>
            ) : null}
        </button>
    );
}

const safeColor = (hex: string) => (/^#[0-9a-f]{3,8}$/i.test(hex) ? hex : "#6b7785");
const tint = (c: string, pct: number) => `color-mix(in srgb, ${c} ${pct}%, #fff)`;

/** The CSS-drawn miniature for a template (see .drive-tpl-* in globals.css). */
function TemplateArt({ t }: { t: DriveTemplate }) {
    const c = safeColor(t.accent);
    const L = (w: number, style?: CSSProperties) => <i style={{ width: `${w}%`, ...style }} />;
    const gap = (h: number) => <span style={{ height: h, flex: "none" }} />;
    const slide = (background: string, fg: string, sub: string) => (
        <span className="drive-tpl-sp" style={{ background }}>
            <b style={{ background: fg }} />
            <i style={{ background: sub }} />
        </span>
    );
    const grid = (chart: boolean) => (
        <span className="drive-tpl-gp">
            <span className="tt" />
            <span className="hr" style={{ background: c }} />
            {chart ? (
                <span className="ch">
                    <span style={{ height: "50%", background: c }} />
                    <span style={{ height: "80%", background: c }} />
                    <span style={{ height: "64%", background: c }} />
                    <span style={{ height: "96%", background: c }} />
                </span>
            ) : null}
        </span>
    );

    switch (t.thumb) {
        case "letter":
            return (
                <span className="drive-tpl-pp">
                    <i className="hd" style={{ background: c, width: "30%" }} />
                    {L(40)}{L(34)}{gap(6)}{L(100)}{L(96)}{L(88)}{L(100)}{L(70)}{gap(4)}{L(100)}{L(92)}{L(60)}
                </span>
            );
        case "invoice":
            return (
                <span className="drive-tpl-pp">
                    <i className="band" style={{ background: c }} />
                    {L(40)}{L(30)}
                    <i className="blk" style={{ background: tint(c, 18) }} />
                    {L(100)}{L(100)}{L(100)}
                    {L(36, { marginLeft: "auto", height: 5, background: c })}
                </span>
            );
        case "resume":
            return (
                <span className="drive-tpl-pp" style={{ flexDirection: "row", padding: 0, gap: 0 }}>
                    <span style={{ width: "34%", background: c }} />
                    <span style={{ flex: 1, padding: "12% 8%", display: "flex", flexDirection: "column", gap: 5 }}>
                        <i className="hd" style={{ background: "#1b1f24", width: "70%" }} />
                        {L(50)}{gap(4)}{L(40, { background: c })}{L(100)}{L(90)}{L(95)}{L(40, { background: c })}{L(100)}{L(80)}
                    </span>
                </span>
            );
        case "report":
            return (
                <span className="drive-tpl-pp" style={{ justifyContent: "flex-end", background: `linear-gradient(180deg, ${c} 0 58%, #fff 58%)` }}>
                    <i className="hd" style={{ background: "#fff", width: "70%", marginBottom: "auto", marginTop: "40%" }} />
                    {L(100)}{L(84)}{L(92)}
                </span>
            );
        case "grid":
            return grid(false);
        case "grid-chart":
            return grid(true);
        case "slide-dark":
            return slide("#1b1f24", c, "#aab2bd");
        case "slide-light":
            return slide(`linear-gradient(135deg, #fff 0 60%, ${tint(c, 16)} 60%)`, c, "#9aa3ad");
        case "slide-teal":
            return slide(tint(c, 12), `color-mix(in srgb, ${c} 80%, #000)`, tint(c, 55));
        case "slide-violet":
            return slide(`linear-gradient(160deg, ${c}, ${tint(c, 55)})`, "#fff", "rgba(255,255,255,.8)");
        case "doc":
            break;
        default:
            if (t.app === "sheets") return grid(false);
            if (t.app === "slides") return slide(`linear-gradient(135deg, #fff 0 60%, ${tint(c, 16)} 60%)`, c, "#9aa3ad");
    }
    return (
        <span className="drive-tpl-pp">
            <i className="hd" style={{ background: c }} />
            {L(50)}{gap(4)}{L(100)}{L(90)}{L(100)}{L(76)}
            <i className="blk" style={{ background: tint(c, 14) }} />
            {L(100)}{L(64)}
        </span>
    );
}
